import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/errors";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48);

export default function PlatformTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", primary_color: "#0f172a", accent_color: "#d4af37",
    logo_url: "", logo_file: null, initial_member_email: "", initial_member_name: "",
  });
  const [saving, setSaving] = useState(false);
  const [tempPass, setTempPass] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/platform/tenants"); setTenants(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name:"", slug:"", primary_color:"#0f172a", accent_color:"#d4af37", logo_url:"", logo_file:null, initial_member_email:"", initial_member_name:"" });
    setTempPass(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) { toast.error("Name and slug required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("slug", form.slug.trim());
      fd.append("primary_color", form.primary_color);
      fd.append("accent_color", form.accent_color);
      if (form.logo_file) fd.append("logo_file", form.logo_file);
      else if (form.logo_url) fd.append("logo_url", form.logo_url);
      if (form.initial_member_email) {
        fd.append("initial_member_email", form.initial_member_email);
        if (form.initial_member_name) fd.append("initial_member_name", form.initial_member_name);
      }
      const { data } = await api.post("/platform/tenants", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Tenant "${data.name}" created`);
      if (data.initial_member_temp_password) {
        setTempPass({ email: form.initial_member_email, password: data.initial_member_temp_password });
      } else {
        setOpen(false);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Create failed");
    } finally { setSaving(false); }
  };

  const del = async (slug) => {
    if (!confirm(`Delete tenant "${slug}"? This is destructive.`)) return;
    try {
      await api.delete(`/platform/tenants/${slug}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  return (
    <div data-testid="platform-tenants" className="mx-auto w-full max-w-6xl">
      <Breadcrumbs />
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">Tenants</h2>
          <p className="mt-1 text-sm text-slate-500">{tenants.length} companies in the group.</p>
        </div>
        <Button onClick={openNew} data-testid="platform-add-tenant" className="gap-2 bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4" />Add tenant
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-10 px-4 py-2" />
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Slug</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Members</th>
              <th className="px-4 py-2 text-right">Apps</th>
              <th className="px-4 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Loading…</td></tr>}
            {tenants.map(t => (
              <tr key={t.id} data-testid={`platform-tenants-row-${t.slug}`} className="border-t border-slate-100">
                <td className="px-4 py-2">
                  {t.branding?.logo_url ? <img src={t.branding.logo_url} className="h-6 w-auto object-contain" alt="" /> : <span className="inline-block h-5 w-5 rounded" style={{background: t.branding?.primary_color}} />}
                </td>
                <td className="px-4 py-2 font-medium text-slate-800">
                  <Link to={`/platform/tenants/${t.slug}`} className="hover:underline">{t.name}</Link>
                </td>
                <td className="px-4 py-2 font-mono text-xs text-slate-500">{t.slug}</td>
                <td className="px-4 py-2 text-xs">
                  <span data-testid={`platform-tenants-status-${t.slug}`} className={t.status === "disabled" ? "rounded bg-amber-100 px-1.5 py-0.5 font-semibold uppercase text-[10px] tracking-wider text-amber-800" : ""}>
                    {t.status}
                  </span>
                  {t.is_platform && <span className="ml-1 rounded bg-slate-900 px-1 py-0.5 text-[9px] text-amber-300">PLATFORM</span>}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">{t.member_count}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{t.grants_count}</td>
                <td className="px-4 py-2 text-right">
                  {!t.is_platform && (
                    <Button size="icon" variant="ghost" onClick={() => del(t.slug)} data-testid={`platform-tenants-delete-${t.slug}`} className="text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4"/></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="platform-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Add tenant</DialogTitle>
            <DialogDescription>Slug is immutable after creation; pick carefully. Logos ≤ 1MB (svg/png/jpg/webp).</DialogDescription>
          </DialogHeader>
          {tempPass ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
              <div className="font-semibold text-slate-800">Initial member created</div>
              <div className="mt-1">Email: <span className="font-mono">{tempPass.email}</span></div>
              <div>Temporary password: <span className="select-all rounded bg-white px-2 py-1 font-mono">{tempPass.password}</span></div>
              <div className="mt-2 text-xs text-slate-500">Copy this now — it won't be shown again.</div>
              <div className="mt-3"><Button onClick={() => setOpen(false)}>Close</Button></div>
            </div>
          ) : (
            <form onSubmit={save} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label>
                  <Input required value={form.name} data-testid="platform-tenant-name" onChange={e => setForm({...form, name:e.target.value, slug: form.slug || slugify(e.target.value)})} />
                </div>
                <div><Label>Slug</Label>
                  <Input required value={form.slug} data-testid="platform-tenant-slug" onChange={e => setForm({...form, slug: slugify(e.target.value)})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Primary</Label>
                  <Input type="color" value={form.primary_color} data-testid="platform-tenant-primary" onChange={e => setForm({...form, primary_color: e.target.value})} />
                </div>
                <div><Label>Accent</Label>
                  <Input type="color" value={form.accent_color} data-testid="platform-tenant-accent" onChange={e => setForm({...form, accent_color: e.target.value})} />
                </div>
              </div>
              <div><Label>Logo</Label>
                <div className="flex gap-2">
                  <Input type="file" accept=".svg,.png,.jpg,.jpeg,.webp" data-testid="platform-tenant-logo-file"
                    onChange={e => setForm({...form, logo_file: e.target.files[0] || null, logo_url: ""})} />
                  <Input placeholder="…or paste URL" value={form.logo_url} data-testid="platform-tenant-logo-url"
                    onChange={e => setForm({...form, logo_url: e.target.value, logo_file: null})} />
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Preview</div>
                <div className="flex items-center gap-2 rounded-md border border-slate-200 p-2">
                  {form.logo_file
                    ? <span className="text-[10px] text-slate-400 flex items-center gap-1"><ImageIcon className="h-3 w-3"/>(local file)</span>
                    : form.logo_url
                      ? <img src={form.logo_url} className="h-6 w-auto" alt="" />
                      : <span className="inline-block h-5 w-5 rounded" style={{background: form.primary_color}} />}
                  <div className="text-sm font-semibold" style={{color: form.primary_color}}>{form.name || "Tenant name"}</div>
                </div>
              </div>

              <div className="mt-2 border-t border-slate-100 pt-3">
                <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-500">Optional: add a tenant admin</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="admin@company.co.za" value={form.initial_member_email} data-testid="platform-tenant-member-email"
                    onChange={e => setForm({...form, initial_member_email: e.target.value})} />
                  <Input placeholder="Full name" value={form.initial_member_name} data-testid="platform-tenant-member-name"
                    onChange={e => setForm({...form, initial_member_name: e.target.value})} />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="platform-tenant-submit" className="bg-slate-900 hover:bg-slate-800">
                  {saving ? "Creating…" : "Create tenant"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
